import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] text-white min-h-screen">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-[#2A2A2A] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="font-['Bebas_Neue'] text-2xl text-white" data-testid="logo-link">
            WAY BIG
          </Link>
          <div className="flex gap-6">
            <Link to="/story" className="text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-sm" data-testid="nav-story-link">
              Story
            </Link>
            <Link to="/" className="text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-sm" data-testid="nav-home-link">
              Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-white uppercase mb-4" data-testid="shop-heading">
              Shop Way Big
            </h1>
            <div className="inline-flex items-center gap-3 bg-[#FF3D00] text-black px-6 py-3 font-['Outfit'] font-bold uppercase text-sm">
              <Lock size={16} />
              <span>Dropping Soon – Join Waitlist for Early Access</span>
            </div>
          </motion.div>

          {loading ? (
            <div className="text-center text-white/60 font-['Manrope']">Loading products...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  data-testid={`product-${product.id}`}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 0.5, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="border border-[#2A2A2A] bg-black/40 p-8 relative overflow-hidden grayscale"
                  style={{ filter: 'grayscale(100%)' }}
                >
                  <div className="absolute top-4 right-4 bg-[#FF3D00] text-black px-3 py-1 font-['Outfit'] font-bold text-xs uppercase">
                    Locked
                  </div>

                  <div className={`pack-mockup ${product.flavor} mx-auto mb-6`}>
                    <div className="pack-content">
                      <div className="text-[#FFB300] font-['Bebas_Neue'] text-xs tracking-[0.2em] mb-2">WAY BIG</div>
                      <div className="text-white font-['Bebas_Neue'] text-2xl tracking-tight">{product.name}</div>
                      <div className="text-white/60 text-xs mt-3">100g</div>
                      <div className="mt-3 space-y-1">
                        <div className="text-[10px] text-white/80">10g Protein</div>
                        <div className="text-[10px] text-white/80">3g Fibre</div>
                      </div>
                    </div>
                  </div>

                  <h3 className="font-['Bebas_Neue'] text-3xl text-white uppercase mb-2">{product.name}</h3>
                  <p className="font-['Manrope'] text-sm text-white/60 mb-4">{product.description}</p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {product.claims.map((claim, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 border border-[#2A2A2A] text-white/70 text-xs uppercase"
                      >
                        {claim}
                      </span>
                    ))}
                  </div>

                  <button
                    disabled
                    className="w-full bg-[#2A2A2A] text-white/40 font-['Outfit'] font-bold text-sm uppercase py-4 cursor-not-allowed"
                    data-testid={`add-to-cart-btn-${product.id}`}
                  >
                    Coming Soon
                  </button>
                </motion.div>
              ))}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-24 bg-[#FF3D00] p-12 text-center"
          >
            <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-black uppercase mb-4">Don't miss the drop</h2>
            <p className="font-['Manrope'] text-lg text-black/80 mb-8">Be the first to know when Way Big launches. Founding members get special pricing.</p>
            <Link
              to="/?scroll=waitlist"
              data-testid="join-waitlist-shop-cta"
              className="inline-block bg-black hover:bg-black/80 text-white font-['Outfit'] font-bold text-sm uppercase tracking-[0.1em] py-4 px-12 border-2 border-black"
            >
              Join the Waitlist
            </Link>
          </motion.div>
        </div>
      </div>

      <footer className="py-16 px-6 bg-black border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="font-['Bebas_Neue'] text-3xl text-white">WAY BIG</div>
            <div className="text-center text-white/50 font-['Manrope'] text-sm">
              Made in India. For people who read labels.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShopPage;